<template>
  <div class="deploy-vm">
    <a-page-header title="Sanal Makine Oluştur" @back="() => $router.go(-1)" />
    <a-spin :spinning="loading">
      <a-row :gutter="24" v-if="app">
        <!-- Sol: Wizard -->
        <a-col :span="16">
          <a-card>
            <a-steps :current="currentStep" style="margin-bottom: 32px">
              <a-step title="Sunucu Detayları" />
              <a-step title="Ağ Ayarları" />
              <a-step title="Ekstra Hizmetler" />
              <a-step title="Onay" />
            </a-steps>

            <!-- Step 0: Sunucu Detayları -->
            <div v-show="currentStep === 0" class="step-content">
              <a-form layout="vertical">
                <a-form-item label="Sunucu Adı" required>
                  <a-input v-model:value="form.name" placeholder="Örn: web-server-01" />
                </a-form-item>
                <a-row :gutter="16">
                  <a-col :span="8">
                    <a-form-item label="CPU (Çekirdek)" required>
                      <a-input-number v-model:value="form.cpuNumber" :min="1" :max="128" style="width: 100%" placeholder="Örn: 2" />
                    </a-form-item>
                  </a-col>
                  <a-col :span="8">
                    <a-form-item label="RAM (GB)" required>
                      <a-input-number v-model:value="form.ramGB" :min="1" :max="512" style="width: 100%" placeholder="Örn: 4" />
                    </a-form-item>
                  </a-col>
                  <a-col :span="8">
                    <a-form-item label="Disk Boyutu (GB)" required>
                      <a-input-number v-model:value="form.diskGB" :min="10" :max="10000" style="width: 100%" placeholder="Örn: 50" />
                    </a-form-item>
                  </a-col>
                </a-row>
                <a-alert message="Custom Offering kullanılacaktır. CPU, RAM ve Disk değerlerini ihtiyacınıza göre belirleyebilirsiniz." type="info" show-icon />
              </a-form>
            </div>

            <!-- Step 1: Ağ Ayarları -->
            <div v-show="currentStep === 1" class="step-content">
              <a-form layout="vertical">
                <a-spin :spinning="networkLoading">
                  <a-form-item label="Network Seçiniz" required>
                    <a-radio-group v-model:value="form.networkId" @change="onNetworkChange" style="width: 100%">
                      <div v-if="allNetworks.length > 0" class="network-list">
                        <div
                          v-for="net in allNetworks"
                          :key="net.id"
                          class="network-item"
                          :class="{ selected: form.networkId === net.id }">
                          <a-radio :value="net.id">
                            <span class="network-name">{{ net.name }}</span>
                            <a-tag size="small" :color="net.type === 'L2' ? 'blue' : 'green'" style="margin-left: 8px">{{ net.type }}</a-tag>
                          </a-radio>
                        </div>
                      </div>
                      <a-empty v-else description="Kullanılabilir ağ bulunamadı" />
                    </a-radio-group>
                  </a-form-item>
                </a-spin>

                <div v-if="isL2Network">
                  <a-divider style="margin: 12px 0" />
                  <a-row :gutter="16">
                    <a-col :span="8">
                      <a-form-item label="IP Adresi" required>
                        <a-input v-model:value="form.ipAddress" placeholder="Örn: 192.168.1.10" />
                      </a-form-item>
                    </a-col>
                    <a-col :span="8">
                      <a-form-item label="Subnet" required>
                        <a-input v-model:value="form.subnet" placeholder="Örn: 255.255.255.0" />
                      </a-form-item>
                    </a-col>
                    <a-col :span="8">
                      <a-form-item label="Gateway" required>
                        <a-input v-model:value="form.gateway" placeholder="Örn: 192.168.1.1" />
                      </a-form-item>
                    </a-col>
                  </a-row>
                </div>
              </a-form>
            </div>

            <!-- Step 2: Ekstra Hizmetler -->
            <div v-show="currentStep === 2" class="step-content">
              <a-form layout="vertical">
                <div class="extra-service-item">
                  <div class="extra-service-info">
                    <CloudServerOutlined class="extra-service-icon" />
                    <div>
                      <div class="extra-service-name">Backup</div>
                      <div class="extra-service-desc">Sunucunuzun düzenli yedeklerini alarak veri kaybına karşı koruma sağlar.</div>
                    </div>
                  </div>
                  <a-switch v-model:checked="form.backupEnabled" />
                </div>
                <div class="extra-service-item">
                  <div class="extra-service-info">
                    <AppstoreAddOutlined class="extra-service-icon" />
                    <div>
                      <div class="extra-service-name">Disaster Recovery</div>
                      <div class="extra-service-desc">Felaket durumunda sunucunuzun farklı bir lokasyonda kopyasını çalıştırarak kesintisiz hizmet sunar.</div>
                    </div>
                  </div>
                  <a-switch v-model:checked="form.drEnabled" />
                </div>
                <div class="extra-service-item">
                  <div class="extra-service-info">
                    <DashboardOutlined class="extra-service-icon" />
                    <div>
                      <div class="extra-service-name">Monitoring</div>
                      <div class="extra-service-desc">Sunucunuzun CPU, RAM, disk ve ağ kullanımını anlık olarak izleyerek performans sorunlarını erken tespit eder.</div>
                    </div>
                  </div>
                  <a-switch v-model:checked="form.monitoringEnabled" />
                </div>
              </a-form>
            </div>

            <!-- Step 3: Onay -->
            <div v-show="currentStep === 3" class="step-content">
              <a-descriptions title="Kurulum Özeti" bordered :column="1">
                <a-descriptions-item label="Ürün">{{ app.name }}</a-descriptions-item>
                <a-descriptions-item label="Kategori">{{ app.category_name || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Versiyon">{{ app.product_version || '-' }}</a-descriptions-item>
                <a-descriptions-item label="İşletim Sistemi">{{ app.product_os || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Template ID">{{ app.template_id || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Sunucu Adı">{{ form.name || '-' }}</a-descriptions-item>
                <a-descriptions-item label="CPU">{{ form.cpuNumber ? form.cpuNumber + ' Çekirdek' : '-' }}</a-descriptions-item>
                <a-descriptions-item label="RAM">{{ form.ramGB ? form.ramGB + ' GB' : '-' }}</a-descriptions-item>
                <a-descriptions-item label="Disk">{{ form.diskGB ? form.diskGB + ' GB' : '-' }}</a-descriptions-item>
                <a-descriptions-item label="Ağ">{{ form.networkName || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Ağ Tipi">{{ selectedNetworkType || '-' }}</a-descriptions-item>
                <a-descriptions-item v-if="isL2Network" label="IP Adresi">{{ form.ipAddress || '-' }}</a-descriptions-item>
                <a-descriptions-item v-if="isL2Network" label="Subnet">{{ form.subnet || '-' }}</a-descriptions-item>
                <a-descriptions-item v-if="isL2Network" label="Gateway">{{ form.gateway || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Backup">
                  <a-tag :color="form.backupEnabled ? 'green' : 'default'">{{ form.backupEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="Disaster Recovery">
                  <a-tag :color="form.drEnabled ? 'green' : 'default'">{{ form.drEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="Monitoring">
                  <a-tag :color="form.monitoringEnabled ? 'green' : 'default'">{{ form.monitoringEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
                </a-descriptions-item>
              </a-descriptions>
            </div>

            <!-- Navigation -->
            <div class="step-actions">
              <a-button v-if="currentStep > 0" @click="currentStep--">
                Geri
              </a-button>
              <a-button v-if="currentStep < 3" type="primary" @click="nextStep">
                İleri
              </a-button>
              <a-button v-if="currentStep === 3" type="primary" :loading="deploying" @click="handleDeploy">
                <template #icon><CloudServerOutlined /></template>
                Sanal Makineyi Oluştur
              </a-button>
            </div>
          </a-card>
        </a-col>

        <!-- Sağ: Canlı Özet -->
        <a-col :span="8">
          <a-card class="summary-card">
            <template #title>
              <div class="summary-header">
                <img v-if="app.icon" :src="app.icon" :alt="app.name" class="summary-icon" />
                <AppstoreOutlined v-else style="font-size: 28px; color: #1890ff" />
                <span>{{ app.name }}</span>
              </div>
            </template>
            <div class="summary-section">
              <div class="summary-label">Kategori</div>
              <div class="summary-value">{{ app.category_name || '-' }}</div>
            </div>
            <div class="summary-section" v-if="app.product_version">
              <div class="summary-label">Versiyon</div>
              <div class="summary-value">{{ app.product_version }}</div>
            </div>
            <div class="summary-section" v-if="app.product_os">
              <div class="summary-label">İşletim Sistemi</div>
              <div class="summary-value">{{ app.product_os }}</div>
            </div>
            <a-divider style="margin: 12px 0" />
            <div class="summary-section">
              <div class="summary-label">Sunucu Adı</div>
              <div class="summary-value" :class="{ placeholder: !form.name }">{{ form.name || 'Belirtilmedi' }}</div>
            </div>
            <div class="summary-section">
              <div class="summary-label">CPU</div>
              <div class="summary-value" :class="{ placeholder: !form.cpuNumber }">{{ form.cpuNumber ? form.cpuNumber + ' Çekirdek' : 'Belirtilmedi' }}</div>
            </div>
            <div class="summary-section">
              <div class="summary-label">RAM</div>
              <div class="summary-value" :class="{ placeholder: !form.ramGB }">{{ form.ramGB ? form.ramGB + ' GB' : 'Belirtilmedi' }}</div>
            </div>
            <div class="summary-section">
              <div class="summary-label">Disk</div>
              <div class="summary-value" :class="{ placeholder: !form.diskGB }">{{ form.diskGB ? form.diskGB + ' GB' : 'Belirtilmedi' }}</div>
            </div>
            <a-divider style="margin: 12px 0" />
            <div class="summary-section">
              <div class="summary-label">Ağ</div>
              <div class="summary-value" :class="{ placeholder: !form.networkName }">{{ form.networkName || 'Belirtilmedi' }}</div>
            </div>
            <div class="summary-section" v-if="selectedNetworkType">
              <div class="summary-label">Ağ Tipi</div>
              <div class="summary-value">
                <a-tag size="small" :color="selectedNetworkType === 'L2' ? 'blue' : 'green'">{{ selectedNetworkType }}</a-tag>
              </div>
            </div>
            <template v-if="isL2Network">
              <div class="summary-section">
                <div class="summary-label">IP Adresi</div>
                <div class="summary-value" :class="{ placeholder: !form.ipAddress }">{{ form.ipAddress || 'Belirtilmedi' }}</div>
              </div>
              <div class="summary-section">
                <div class="summary-label">Subnet</div>
                <div class="summary-value" :class="{ placeholder: !form.subnet }">{{ form.subnet || 'Belirtilmedi' }}</div>
              </div>
              <div class="summary-section">
                <div class="summary-label">Gateway</div>
                <div class="summary-value" :class="{ placeholder: !form.gateway }">{{ form.gateway || 'Belirtilmedi' }}</div>
              </div>
            </template>
            <a-divider style="margin: 12px 0" />
            <div class="summary-section">
              <div class="summary-label">Backup</div>
              <div class="summary-value">
                <a-tag size="small" :color="form.backupEnabled ? 'green' : 'default'">{{ form.backupEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
              </div>
            </div>
            <div class="summary-section">
              <div class="summary-label">Disaster Recovery</div>
              <div class="summary-value">
                <a-tag size="small" :color="form.drEnabled ? 'green' : 'default'">{{ form.drEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
              </div>
            </div>
            <div class="summary-section">
              <div class="summary-label">Monitoring</div>
              <div class="summary-value">
                <a-tag size="small" :color="form.monitoringEnabled ? 'green' : 'default'">{{ form.monitoringEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
              </div>
            </div>
          </a-card>
        </a-col>
      </a-row>
    </a-spin>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { message, notification } from 'ant-design-vue'
import { getAPI, postAPI } from '@/api'
import {
  AppstoreOutlined,
  AppstoreAddOutlined,
  CloudServerOutlined,
  DashboardOutlined
} from '@ant-design/icons-vue'

export default {
  name: 'MarketPlaceDeployVM',
  components: {
    AppstoreOutlined,
    AppstoreAddOutlined,
    CloudServerOutlined,
    DashboardOutlined
  },
  setup () {
    const route = useRoute()
    const router = useRouter()
    const instance = getCurrentInstance()
    const app = ref(null)
    const loading = ref(false)
    const deploying = ref(false)
    const currentStep = ref(0)
    const networkLoading = ref(false)
    const allNetworks = ref([])
    const customOffering = ref(null)
    const zoneId = ref(null)

    const form = reactive({
      name: '',
      cpuNumber: null,
      ramGB: null,
      diskGB: null,
      networkId: null,
      networkName: '',
      ipAddress: '',
      gateway: '',
      subnet: '',
      backupEnabled: false,
      drEnabled: false,
      monitoringEnabled: false
    })

    const selectedNetworkType = computed(() => {
      if (!form.networkId) return null
      const net = allNetworks.value.find(n => n.id === form.networkId)
      return net ? net.type : null
    })

    const isL2Network = computed(() => {
      return selectedNetworkType.value === 'L2'
    })

    const fetchProduct = async () => {
      loading.value = true
      try {
        const slug = route.params.slug
        const res = await axios.get(`https://cs-portal.bulutpark.tr/api/marketplace/products/${slug}`)
        app.value = res.data
      } catch (error) {
        console.error('Error fetching product:', error)
        message.error('Ürün bilgisi yüklenemedi')
      } finally {
        loading.value = false
      }
    }

    const fetchNetworks = () => {
      networkLoading.value = true
      getAPI('listNetworks', {
        listall: true
      }).then(response => {
        allNetworks.value = response.listnetworksresponse.network || []
      }).catch(() => {
        message.error('Ağ listesi yüklenemedi')
        allNetworks.value = []
      }).finally(() => {
        networkLoading.value = false
      })
    }

    const fetchCustomOffering = () => {
      getAPI('listServiceOfferings', {
        issystem: false
      }).then(response => {
        const offerings = response.listserviceofferingsresponse.serviceoffering || []
        console.log('All offerings:', offerings.map(o => ({ name: o.name, id: o.id, iscustomized: o.iscustomized })))
        const custom = offerings.find(o => o.iscustomized === true)
        if (custom) {
          customOffering.value = custom
          console.log('Custom offering found:', custom.name, custom.id)
        } else {
          console.warn('No custom service offering found, available:', offerings.map(o => o.name))
        }
      }).catch(error => {
        console.error('listServiceOfferings error:', error)
      })
    }

    const fetchZone = () => {
      getAPI('listZones', { available: true }).then(response => {
        const zones = response.listzonesresponse.zone || []
        if (zones.length > 0) {
          zoneId.value = zones[0].id
          console.log('Zone found:', zones[0].name, zones[0].id)
        }
      }).catch(error => {
        console.error('listZones error:', error)
      })
    }

    const onNetworkChange = (e) => {
      const net = allNetworks.value.find(n => n.id === e.target.value)
      form.networkName = net ? net.name : ''
      form.ipAddress = ''
      form.gateway = ''
      form.subnet = ''
    }

    const nextStep = () => {
      if (currentStep.value === 0) {
        if (!form.name || !form.name.trim()) {
          message.warning('Sunucu adı zorunludur')
          return
        }
        if (!form.cpuNumber || form.cpuNumber < 1) {
          message.warning('CPU değeri giriniz')
          return
        }
        if (!form.ramGB || form.ramGB < 1) {
          message.warning('RAM değeri giriniz')
          return
        }
        if (!form.diskGB || form.diskGB < 10) {
          message.warning('Disk boyutu en az 10 GB olmalıdır')
          return
        }
      }
      if (currentStep.value === 1) {
        if (!form.networkId) {
          message.warning('Bir ağ seçiniz')
          return
        }
        if (isL2Network.value) {
          if (!form.ipAddress || !form.ipAddress.trim()) {
            message.warning('IP adresi zorunludur')
            return
          }
          if (!form.subnet || !form.subnet.trim()) {
            message.warning('Subnet zorunludur')
            return
          }
          if (!form.gateway || !form.gateway.trim()) {
            message.warning('Gateway zorunludur')
            return
          }
        }
      }
      currentStep.value++
    }

    const CS_PORTAL_API = 'https://cs-portal.bulutpark.tr/api'
    let mpRequestId = null

    const handleDeploy = async () => {
      if (!app.value || !app.value.template_id) {
        message.error('Template ID bulunamadı')
        return
      }
      if (!customOffering.value) {
        message.error('Custom Service Offering bulunamadı')
        return
      }
      if (!zoneId.value) {
        message.error('Zone bilgisi bulunamadı')
        return
      }

      deploying.value = true

      // 1. cs-portal'a talep kaydı oluştur (fire-and-forget)
      try {
        const requestData = {
          product_id: app.value.id,
          product_name: app.value.name,
          vm_name: form.name,
          cpu: form.cpuNumber,
          ram: form.ramGB * 1024,
          disk: form.diskGB,
          network_name: form.networkName,
          network_type: selectedNetworkType.value,
          ip_address: form.ipAddress || null,
          subnet: form.subnet || null,
          gateway: form.gateway || null,
          backup_enabled: form.backupEnabled,
          dr_enabled: form.drEnabled,
          monitoring_enabled: form.monitoringEnabled,
          template_id: app.value.template_id,
          zone_id: zoneId.value,
          requested_by: instance.proxy.$store?.getters?.userInfo?.username || 'unknown',
          domain_name: instance.proxy.$store?.getters?.userInfo?.domain || null
        }
        const mpRes = await axios.post(`${CS_PORTAL_API}/marketplace/requests`, requestData)
        mpRequestId = mpRes.data.id
        console.log('MP Talep created, id:', mpRequestId)
      } catch (err) {
        console.warn('cs-portal talep kaydı oluşturulamadı, deploy devam ediyor:', err.message)
      }

      // 2. CloudStack deploy başlat
      const deployData = {
        name: form.name,
        displayname: form.name,
        zoneid: zoneId.value,
        templateid: app.value.template_id,
        serviceofferingid: customOffering.value.id,
        'details[0].cpuNumber': form.cpuNumber,
        'details[0].cpuSpeed': 1000,
        'details[0].memory': form.ramGB * 1024,
        rootdisksize: form.diskGB,
        startvm: true,
        'iptonetworklist[0].networkid': form.networkId
      }

      if (isL2Network.value && form.ipAddress) {
        deployData['iptonetworklist[0].ip'] = form.ipAddress
      }

      console.log('Deploy VM data:', deployData)

      postAPI('deployVirtualMachine', deployData).then(response => {
        const jobId = response.deployvirtualmachineresponse.jobid

        // Hemen instances sayfasına yönlendir
        router.push({ name: 'vm' })

        instance.proxy.$pollJob({
          jobId: jobId,
          title: 'Sanal Makine Oluştur',
          description: form.name,
          successMessage: 'Sanal makine başarıyla oluşturuldu',
          successMethod: (result) => {
            deploying.value = false
            // cs-portal status → deployed
            if (mpRequestId) {
              const vmId = result?.jobresult?.virtualmachine?.id || null
              axios.put(`${CS_PORTAL_API}/marketplace/requests/${mpRequestId}/status`, {
                status: 'deployed',
                cloudstack_vm_id: vmId
              }).catch(err => console.warn('cs-portal status güncellenemedi:', err.message))
            }
          },
          errorMessage: 'Sanal makine oluşturulamadı',
          errorMethod: () => {
            deploying.value = false
            // 3. cs-portal status → failed
            if (mpRequestId) {
              axios.put(`${CS_PORTAL_API}/marketplace/requests/${mpRequestId}/status`, {
                status: 'failed'
              }).catch(err => console.warn('cs-portal status güncellenemedi:', err.message))
            }
          },
          catchMethod: () => {
            deploying.value = false
          }
        })
      }).catch(error => {
        deploying.value = false
        console.error('deployVirtualMachine error:', error)
        const responseData = error?.response?.data || error?.data || {}
        const csResponse = responseData?.deployvirtualmachineresponse || {}
        console.error('CloudStack error:', JSON.stringify(csResponse))
        const errorText = csResponse?.errortext || error?.message || 'Bilinmeyen hata'
        notification.error({
          message: 'Sanal Makine Oluşturulamadı',
          description: errorText
        })
        // cs-portal status → failed
        if (mpRequestId) {
          axios.put(`${CS_PORTAL_API}/marketplace/requests/${mpRequestId}/status`, {
            status: 'failed'
          }).catch(err => console.warn('cs-portal status güncellenemedi:', err.message))
        }
      })
    }

    onMounted(() => {
      fetchProduct()
      fetchNetworks()
      fetchCustomOffering()
      fetchZone()
    })

    return {
      app,
      loading,
      deploying,
      currentStep,
      networkLoading,
      allNetworks,
      selectedNetworkType,
      isL2Network,
      form,
      onNetworkChange,
      nextStep,
      handleDeploy
    }
  }
}
</script>

<style scoped>
.step-content {
  min-height: 250px;
  padding: 24px 0;
}
.step-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
}

/* Sağ Özet Panel */
.summary-card {
  position: sticky;
  top: 80px;
}
.summary-header {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 600;
}
.summary-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}
.summary-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}
.summary-label {
  color: #888;
  font-size: 13px;
}
.summary-value {
  font-weight: 600;
  font-size: 13px;
  color: #23272f;
}
.summary-value.placeholder {
  color: #d9d9d9;
  font-weight: 400;
}

/* Network Listesi */
.network-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.network-item {
  padding: 10px 16px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  transition: all 0.2s;
}
.network-item:hover {
  border-color: #1890ff;
  background: #f0f7ff;
}
.network-item.selected {
  border-color: #1890ff;
  background: #e6f4ff;
}
.network-name {
  font-weight: 500;
}

/* Ekstra Hizmetler */
.extra-service-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 12px;
  transition: all 0.2s;
}
.extra-service-item:hover {
  border-color: #1890ff;
  background: #fafafa;
}
.extra-service-info {
  display: flex;
  align-items: center;
  gap: 14px;
}
.extra-service-icon {
  font-size: 28px;
  color: #1890ff;
}
.extra-service-name {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 4px;
}
.extra-service-desc {
  color: #888;
  font-size: 13px;
}
</style>
